// Relaties "Media & documenten" tile — the authenticated blob fetch the generic Refine dataProvider
// can't make. The access token is bearer-only (in memory, not a cookie), so a plain <img src> can't
// carry it; image thumbnails + downloads go through an authenticated fetch → object URL.
import { getAccessToken } from "../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** Fetch a relation-file's bytes with the bearer token (for an image thumbnail or a download). */
export async function fetchRelatieMediaBlob(relatieId: string, fileId: number): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_URL}/relaties/${relatieId}/media/${fileId}/content`, { headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

/** A failed media call, with the status the tile branches on (409 = someone else edited it first). */
export class RelatieMediaError extends Error {
  constructor(public readonly statusCode: number, message: string) {
    super(message);
    this.name = "RelatieMediaError";
  }
}

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Upload one or more files against a relation, with a shared description + tag selection. */
export async function uploadRelatieMedia(
  relatieId: string,
  files: File[],
  description: string,
  tagIds: number[],
): Promise<void> {
  const form = new FormData();
  for (const file of files) form.append("files", file);
  if (description.trim()) form.append("description", description.trim());
  for (const id of tagIds) form.append("tagIds", String(id));

  const res = await fetch(`${API_URL}/relaties/${relatieId}/media`, {
    method: "POST",
    headers: authHeaders(), // no Content-Type — the browser sets the multipart boundary
    body: form,
  });
  if (!res.ok) throw new RelatieMediaError(res.status, res.statusText);
}

/** Soft-delete a relation file (the row stays, flagged, exactly as Jmix does it). */
export async function deleteRelatieMedia(relatieId: string, fileId: number): Promise<void> {
  const res = await fetch(`${API_URL}/relaties/${relatieId}/media/${fileId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new RelatieMediaError(res.status, res.statusText);
}

/** Trigger a browser download of a relation file. */
export async function downloadRelatieMedia(relatieId: string, fileId: number, fileName: string): Promise<void> {
  const blob = await fetchRelatieMediaBlob(relatieId, fileId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "bestand";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
